// Bridge BS3's tooltip/popover API differences onto BS5.
//
// 1) Instance shape: legacy product JS (tooltip.js, popover.js, controllers)
//    reads `$(el).data('bs.tooltip')` and pokes BS3 internals (`.$tip`,
//    `.$element`, `.tip()`, `.inState`). BS5 keeps its instance in its own
//    registry with a different shape, so we mirror a BS3-shaped proxy into
//    jQuery's data store after every plugin call.
//
// 2) Options: legacy call sites pass BS3 options BS5 no longer understands —
//    `placement: 'auto bottom'` (BS5 wants a single placement), `viewport`
//    (removed) and `whiteList` (renamed allowList). Normalize before calling.
//
// 3) Tip markup: BS3 stamped `.in` and the placement (`top`/`bottom`/...) as
//    classes on the tip; custom_bootstrap styling keys off those. Mirror them
//    from BS5's `.show` + data-popper-placement when a tip is shown.
import { upgradeElement } from './data-attr-bridge';

export function installLegacyInstanceBridge($, { Tooltip, Popover }) {
  // BS3 stripped the `auto` token from placements wherever it appeared —
  // 'auto bottom' AND 'top auto' (both exist in legacy markup) — falling back
  // to 'top' when nothing remained. BS5 knows no such strings: they resolve to
  // an undefined Popper attachment and the tip renders unpositioned at 0,0.
  const normalizePlacement = (value) => {
    if (typeof value !== 'string' || !/\bauto\b/i.test(value)) {return value;}

    return value.replace(/\s*\bauto\b\s*/gi, ' ').trim().toLowerCase() || 'top';
  };

  const normalizeConfig = (config) => {
    if (config === 'destroy') {return 'dispose';} // renamed in BS4+
    if (!config || typeof config !== 'object') {return config;}

    const out = { ...config };

    if (typeof out.placement === 'string') {
      out.placement = normalizePlacement(out.placement);
    } else if (typeof out.placement === 'function') {
      const original = out.placement;

      // Normal function (not arrow): BS5 calls the placement callback with the
      // component instance as `this`, and BS3 callbacks may read it — forward
      // that receiver to the original instead of capturing this closure's.
      out.placement = function (tip, trigger) {
        return normalizePlacement(original.call(this, tip, trigger));
      };
    }

    if ('whiteList' in out) {
      out.allowList = out.allowList || out.whiteList;
      delete out.whiteList;
    }
    delete out.viewport;

    return out;
  };

  const bridge = (name, Component) => {
    const dataKey = `bs.${name}`;
    const jqInterface = Component.jQueryInterface;

    const proxyFor = (inst) => ({
      _bs5: inst,
      // Create-on-demand, like BS3's tip(): legacy `show.bs.*` handlers read
      // `.$tip` to stamp classes (data-tipclass) BEFORE BS5 builds the tip.
      get $tip() {
        if (!inst.tip && typeof inst._getTipElement === 'function') {inst._getTipElement();}

        return inst.tip ? $(inst.tip) : $();
      },
      get $element() { return $(inst._element); },
      get inState() { return inst._activeTrigger || {}; },
      tip() {
        if (!inst.tip && typeof inst._getTipElement === 'function') {inst._getTipElement();}

        return $(inst.tip);
      },
    });

    $.fn[name] = function (config) {
      // Mirror data-* -> data-bs-* NOW: the observer-based mirror is async and
      // BS5 snapshots data-bs-* config at instantiation (data-placement etc.
      // was being ignored for tooltips created right after their element).
      this.each(function () { upgradeElement(this); });
      const normalized = normalizeConfig(config);
      let ret = this;

      if (normalized && typeof normalized === 'object') {
        // Legacy code stamps extra tip classes from `data-tipclass` via
        // `.$tip.addClass(...)` in its show handler; BS5 throws that tip away
        // right after the show event and builds a fresh one, so route the
        // class through BS5's own customClass (applied on every rebuild,
        // before Popper measures the tip).
        this.each(function () {
          const tipclass = $(this).data('tipclass');
          const cfg = tipclass && !normalized.customClass ? { ...normalized, customClass: tipclass } : normalized;

          jqInterface.call($(this), cfg);
        });
      } else {
        ret = jqInterface.call(this, normalized);
      }

      this.each(function () {
        const inst = Component.getInstance(this);

        // No live instance = it was just disposed ('destroy'/'dispose'); drop
        // the stale proxy so legacy `.data('bs.tooltip')` reads don't return a
        // handle to a gone instance.
        if (inst) {$(this).data(dataKey, proxyFor(inst));}
        else {$(this).removeData(dataKey);}
      });

      return ret;
    };
    $.fn[name].Constructor = Component;

    // BS3's default tip margin on the placement axis (replaced, not added to,
    // by app CSS like `.tooltip.my-account-avatar-tooltip { margin-top: 7px }`).
    // The [0, 8] offset in bs3_compat.js already accounts for these 3px.
    const BS3_PLACEMENT_MARGIN = {
      top: ['margin-top', -3],
      bottom: ['margin-top', 3],
      left: ['margin-left', -3],
      right: ['margin-left', 3],
    };

    // BS5 stamps `margin: 0` inline, muting app CSS that nudged BS3 tips via
    // margins (e.g. .my-account-avatar-tooltip). Let those margins through
    // again; on the placement axis they REPLACED BS3's default 3px, so
    // subtract what the [0, 8] offset already covers. Idempotent: clears its
    // own longhands before reading the CSS-only value.
    const applyLegacyMargins = (tip) => {
      tip.style.removeProperty('margin');
      tip.style.removeProperty('margin-top');
      tip.style.removeProperty('margin-left');
      const axis = BS3_PLACEMENT_MARGIN[(tip.getAttribute('data-popper-placement') || '').split('-')[0]];

      if (!axis) {return;}
      const [prop, bs3Default] = axis;
      const cssMargin = parseFloat(getComputedStyle(tip).getPropertyValue(prop)) || 0;

      if (cssMargin) {tip.style.setProperty(prop, `${cssMargin - bs3Default}px`);}
    };

    // Geometry must land before the first paint: `shown` only fires after the
    // fade completes, and a margin applied there reads as a visible jump
    // (BS3's margins were plain CSS, in place from the start). At `inserted`
    // Popper hasn't resolved the placement yet; by the pre-paint rAF it has.
    document.addEventListener(`inserted.bs.${name}`, (event) => {
      const tip = Component.getInstance(event.target)?.tip;

      if (tip) {requestAnimationFrame(() => applyLegacyMargins(tip));}
    });

    // BS3 stamped `in` on the visible tip and legacy code checks
    // `.hasClass('in')`. The BS3 placement classes (top/bottom/...) are NOT
    // mirrored: they'd pull BS3 arrow/padding geometry from application.css
    // on top of the popper-positioned BS5 tip (double arrow, position jump).
    document.addEventListener(`shown.bs.${name}`, (event) => {
      const tip = Component.getInstance(event.target)?.tip;

      if (!tip) {return;}
      tip.classList.add('in');
      applyLegacyMargins(tip); // safety net if the rAF ran before Popper resolved
    });
  };

  bridge('tooltip', Tooltip);
  bridge('popover', Popover);
}
